import React from 'react';
import { Container, Box, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { motion } from 'framer-motion';
import DynamicTyping from '../../components/DynamicTyping';
import Timeline from '../../components/Timeline/Timeline';
import Seo from '../../components/Seo';
import homeConfig from '../../assets/configs/homeConfig';
import Hero from '../../components/Hero';
import { useLang } from '../../utils/i18n';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } },
};

export default function Home() {
  const [lang] = useLang();
  const canonical = typeof window !== 'undefined' ? window.location.href : undefined;

  const titles = homeConfig.titles_i18n?.[lang] || homeConfig.titles || ['Data Scientist'];

  const timelineItems = (homeConfig.workTimeline || []).map((it) => ({
    ...it,
    title: it.title_i18n?.[lang] || it.title,
    description: it.description_i18n?.[lang] || it.description,
  }));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Martín Antúnez',
    url: canonical,
    jobTitle: titles?.[0] || 'Data Scientist',
  };

  const greetingNode = homeConfig.greeting_i18n?.[lang] || homeConfig.greeting;
  const ExperienceLabel = lang === 'es' ? 'Experiencia' : 'Experience';

  return (
    <section>
      <Seo
        title={lang === 'es' ? 'Martín Antúnez — Científico de Datos de Salud e IA' : 'Martín Antúnez — Healthcare AI & Data Scientist'}
        description={
          lang === 'es'
            ? 'Ciencia de datos y GenAI para salud: modelos predictivos clínicos, RAG, agentes de IA, analítica en Azure y GCP. Proyectos, artículos y experiencia.'
            : 'Healthcare AI & data science: clinical predictive models, RAG, agentic AI, analytics on Azure & GCP. Selected projects, writing, and experience.'
        }
        canonical={canonical}
        jsonLd={jsonLd}
      />

      <Hero />

      {/* About / intro section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <Grid2 container spacing={{ xs: 6, md: 8 }} alignItems="center" justifyContent="center">
            {/* Text side */}
            <Grid2 xs={12} md={6}>
              <Box sx={{ mb: 1.5 }}>
                {React.isValidElement(greetingNode) ? (
                  <Box
                    sx={{
                      '& .heading': {
                        margin: 0,
                        fontSize: { xs: 36, md: 52 },
                        fontWeight: 900,
                        letterSpacing: -1,
                        color: 'text.primary',
                        lineHeight: 1.15,
                      },
                      '& .main-name': {
                        color: '#06B6D4',
                      },
                    }}
                  >
                    {greetingNode}
                  </Box>
                ) : (
                  <Typography variant="h3" sx={{ fontWeight: 900, mb: 0 }}>
                    {greetingNode}
                  </Typography>
                )}
              </Box>
              <Box sx={{ fontSize: { xs: 20, md: 26 }, color: 'primary.main', fontWeight: 600, mb: 0 }}>
                <DynamicTyping titles={titles} />
              </Box>
            </Grid2>

            {/* Photo side */}
            <Grid2 xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={homeConfig.profilePicture}
                alt="Martín Antúnez"
                sx={{
                  width: { xs: '70%', sm: 300, md: 380 },
                  height: 'auto',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.07)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  objectFit: 'cover',
                  filter: 'brightness(0.95)',
                }}
              />
            </Grid2>
          </Grid2>
        </motion.div>
      </Container>

      {/* Experience section */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
          >
            <Typography
              component="h2"
              variant="h3"
              sx={{ fontWeight: 900, mb: 6, letterSpacing: -0.5 }}
            >
              {ExperienceLabel}
            </Typography>
          </motion.div>
          <Box sx={{ width: '100%', maxWidth: 960 }}>
            <Timeline items={timelineItems} />
          </Box>
        </Container>
      </Box>
    </section>
  );
}
